import { GetRecommendationByIdApiResponse } from '@/api'
import { Typography } from '@/components'
import { Breakpoint, ThemeColor } from '@/enums'
import { AdaptiveTooltip, Card } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { RecommendationRadio } from '../RecommendationRadio/RecommendationRadio'
import { Info } from 'lucide-react'
import { useResetRecommendationOptions } from '@/services'
import { themeBreakpointUp } from '@/utils'
import { useSegment } from '@/modules'
import { RecommendationToggle } from '../RecommendationToggle/RecommendationToggle'
import {
  SupportedCustomizationOptionsKeys,
  SupportedCustomizationSearchCriteriaKeys,
} from '@/types/recommendation'

const BasicProtectionKeys = [
  SupportedCustomizationOptionsKeys.TRAFFIC_COVERAGE,
  SupportedCustomizationOptionsKeys.EMPLOYER_LABOR_LAW_PROTECTION_AREA,
  SupportedCustomizationOptionsKeys.REAL_ESTATE_COVERAGE,
]

const ExtendedProtectionKeys = [
  SupportedCustomizationOptionsKeys.SPECIAL_CRIMINAL_LEGAL_PROTECTION_INSURANCE,
  SupportedCustomizationOptionsKeys.PRIVATE_GENERAL_LEGAL_PROTECTION,
  SupportedCustomizationOptionsKeys.COMPANY_CONTRACT_LEGAL_PROTECTION,
]

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 16px;
`

const CardHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > *:first-child {
    order: 2;
  }

  & > *:last-child {
    align-self: flex-end;
    margin-bottom: 12px;
  }

  ${themeBreakpointUp(Breakpoint.tab)} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    & > *:first-child {
      order: initial;
    }

    & > *:last-child {
      align-self: center;
      margin-bottom: 0px;
    }
  }
`

const SubsectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
`

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const InfoIconWrapper = styled.div`
  cursor: pointer;
`

interface Props {
  shouldShowRecommendationCustomization: boolean
  recommendationData: GetRecommendationByIdApiResponse
}

const isAllRecommended = (data: GetRecommendationByIdApiResponse) => {
  if (!data.customization || !data.searchCriteria) return false

  return Object.entries(data.customization).every(([key, options]) => {
    const currentValue = data.searchCriteria?.[
      key as keyof typeof data.searchCriteria
    ] as string
    const matched = options.find((option) => {
      // cheks if this is a search criteria or an attribute option
      if ('value' in option) {
        return option.value === currentValue
      }
      return false
    })
    return matched?.isRecommended === true
  })
}

export const RecommendationCustomization = ({
  recommendationData,
  shouldShowRecommendationCustomization,
}: Props) => {
  const { t } = useTranslation()
  const analytics = useSegment()

  const resetMutation = useResetRecommendationOptions(
    recommendationData.recommendationId,
  )
  const shouldShowCustomization =
    shouldShowRecommendationCustomization && recommendationData?.customization
  const amountInsuredData =
    recommendationData.customization?.[
      SupportedCustomizationSearchCriteriaKeys.AMOUNT_INSURED
    ]
  const deductiblesData =
    recommendationData.customization?.[
      SupportedCustomizationSearchCriteriaKeys.DEDUCTIBLES
    ]
  const basicProtectionData = !!recommendationData.customization?.options
    ? Object.values(recommendationData.customization.options).filter((option) =>
        BasicProtectionKeys.includes(
          option.name as SupportedCustomizationOptionsKeys,
        ),
      )
    : []
  const basicProtectionValues =
    recommendationData.searchCriteria?.options?.filter((item) => {
      return !!basicProtectionData.find(
        (basicProtectionItem) => basicProtectionItem.name === item.name,
      )
    }) || []
  const shouldShowBasicProtection =
    basicProtectionData.length > 0 && basicProtectionValues.length > 0
  const extendedProtectionData = !!recommendationData.customization?.options
    ? Object.values(recommendationData.customization.options).filter((option) =>
        ExtendedProtectionKeys.includes(
          option.name as SupportedCustomizationOptionsKeys,
        ),
      )
    : []
  const extendedProtectionValues =
    recommendationData.searchCriteria?.options?.filter((item) => {
      return !!extendedProtectionData.find(
        (extendedProtectionItem) => extendedProtectionItem.name === item.name,
      )
    }) || []
  const shouldShowExtendedProtection =
    extendedProtectionData.length > 0 && extendedProtectionValues.length > 0
  const shouldShowResetButton = !isAllRecommended(recommendationData)

  const handleCustomizationReset = async () => {
    await resetMutation.mutateAsync({
      recommendationId: recommendationData.recommendationId,
    })

    await analytics.track('recommendation_reset_customization')
  }

  if (!shouldShowCustomization) return null

  return (
    <Card variant="borderless" data-testid="recommendation-customization">
      <CardContainer>
        <CardHeaderContainer>
          <Typography variant="itemTitle">
            {t('recommendations.customization.sectionTitle')}
          </Typography>
          {shouldShowResetButton ? (
            <Typography
              variant="footnote"
              color={ThemeColor.danger1}
              onClick={handleCustomizationReset}
            >
              {t('recommendations.customization.resetOfferButtonLabel')}
            </Typography>
          ) : null}
        </CardHeaderContainer>

        {amountInsuredData &&
        recommendationData.searchCriteria?.amountInsured ? (
          <SubsectionContainer>
            <SectionTitleWrapper>
              <Typography variant="subheadline">
                {t(
                  'recommendations.customization.amountInsured.insuranceSumTitle',
                )}
              </Typography>
              <AdaptiveTooltip
                content={t(
                  'recommendations.customization.amountInsured.sectionInfoMessage',
                )}
              >
                <InfoIconWrapper>
                  <Info height={24} />
                </InfoIconWrapper>
              </AdaptiveTooltip>
            </SectionTitleWrapper>

            <div>
              <RecommendationRadio
                recommendationId={recommendationData.recommendationId}
                optionKey={
                  SupportedCustomizationSearchCriteriaKeys.AMOUNT_INSURED
                }
                options={amountInsuredData}
                initialValue={recommendationData.searchCriteria.amountInsured}
              />
            </div>
          </SubsectionContainer>
        ) : null}

        {deductiblesData && recommendationData.searchCriteria?.deductible ? (
          <SubsectionContainer>
            <SectionTitleWrapper>
              <Typography variant="subheadline">
                {t('recommendations.customization.deductible.deductiblesTitle')}
              </Typography>
              <AdaptiveTooltip
                content={t(
                  'recommendations.customization.deductible.sectionInfoMessage',
                )}
              >
                <InfoIconWrapper>
                  <Info height={24} />
                </InfoIconWrapper>
              </AdaptiveTooltip>
            </SectionTitleWrapper>
            <div>
              <RecommendationRadio
                recommendationId={recommendationData.recommendationId}
                optionKey={SupportedCustomizationSearchCriteriaKeys.DEDUCTIBLES}
                options={deductiblesData}
                initialValue={recommendationData.searchCriteria.deductible}
              />
            </div>
          </SubsectionContainer>
        ) : null}

        {shouldShowBasicProtection ? (
          <SubsectionContainer>
            <SectionTitleWrapper>
              <Typography variant="subheadline">
                {t(
                  'recommendations.customization.basicProtection.sectionTitle',
                )}
              </Typography>
              <AdaptiveTooltip
                content={t(
                  'recommendations.customization.basicProtection.sectionInfoMessage',
                )}
              >
                <InfoIconWrapper>
                  <Info height={24} />
                </InfoIconWrapper>
              </AdaptiveTooltip>
            </SectionTitleWrapper>
            {basicProtectionData.map((itemData) => {
              const value = recommendationData.searchCriteria?.options?.find(
                (option) => itemData.name === option.name,
              )?.value

              if (value === undefined) return null

              return (
                <div>
                  <RecommendationToggle
                    optionKey={itemData.name}
                    isRecommended={itemData.isRecommended}
                    recommendationId={recommendationData.recommendationId}
                    initialValue={value}
                  />
                </div>
              )
            })}
          </SubsectionContainer>
        ) : null}

        {shouldShowExtendedProtection ? (
          <SubsectionContainer>
            <SectionTitleWrapper>
              <Typography variant="subheadline">
                {t(
                  'recommendations.customization.extendedProtection.sectionTitle',
                )}
              </Typography>
              <AdaptiveTooltip
                content={t(
                  'recommendations.customization.extendedProtection.sectionInfoMessage',
                )}
              >
                <InfoIconWrapper>
                  <Info height={24} />
                </InfoIconWrapper>
              </AdaptiveTooltip>
            </SectionTitleWrapper>
            {extendedProtectionData.map((itemData) => {
              const value = recommendationData.searchCriteria?.options?.find(
                (option) => itemData.name === option.name,
              )?.value

              if (value === undefined) return null

              return (
                <div>
                  <RecommendationToggle
                    optionKey={itemData.name}
                    isRecommended={itemData.isRecommended}
                    recommendationId={recommendationData.recommendationId}
                    initialValue={value}
                  />
                </div>
              )
            })}
          </SubsectionContainer>
        ) : null}
      </CardContainer>
    </Card>
  )
}
